import { AsyncPipe } from '@angular/common';
import { Component, Input, OnChanges, inject } from '@angular/core';

import { ParticipantOption, ParticipantPriceType } from '@cds/betting-offer';
import { AutomationDirective } from '@frontend/sports/automation/core-feature';
import { exhaustiveCheckGuard } from '@frontend/sports/common/core/utils/extended-types';
import { CustomActiveStateDirective } from '@frontend/sports/common/ui/custom-active-state';
import { HapticsDirective } from '@frontend/sports/haptic-feedback/feature';
import { NativePrice } from '@frontend/sports/odds/feature/native-price';
import { PickSourceProvider } from '@frontend/sports/option/pick/feature/tracking';
import { WinGameFixture, WinGameParticipant } from '@frontend/sports/racing/feature/models';
import { Store } from '@ngrx/store';

import { ExternalBetslipActions } from '../../betslip/core/external-betslip-actions';
import { V2WinPickId } from '../../betslip/core/picks/pick-id';
import { PriceType } from '../../betslip/core/picks/pick-models';
import { HorsesFixturePricingHelper } from '../horse-racing-shared/services/horses-fixture-pricing-helper.service';
import { OptionIndicatorDirective } from './option-indicator.directive';
import { PickComponent } from './pick.component';

@Component({
    selector: 'ms-fixture-pick',
    templateUrl: './pick.component.html',
    imports: [CustomActiveStateDirective, OptionIndicatorDirective, AutomationDirective, AsyncPipe, HapticsDirective],
})
export class FixturePickComponent extends PickComponent implements OnChanges {
    private horsesFixturePricingHelper = inject(HorsesFixturePricingHelper);
    private pickSourceProvider = inject(PickSourceProvider);
    private store = inject(Store);

    @Input() participant: WinGameParticipant;
    @Input() fixture: WinGameFixture;
    @Input() option: ParticipantOption;
    @Input() enablePriceTypeCheck = false;
    @Input() parentLinkedEventId?: string;
    @Input() trackingOptions?: { [key: string]: string };

    override pickId: V2WinPickId;

    ngOnChanges(): void {
        this.state.locked = this.horsesFixturePricingHelper.isPriceLocked(this.fixture, this.participant, this.option);
        this.setPickId(this.createPickId(), this.enablePriceTypeCheck ? this.getPriceType(this.option.priceType) : undefined);
    }

    private createPickId(): V2WinPickId {
        return new V2WinPickId(this.fixture.id, this.option.marketId, this.participant.id);
    }

    get price(): NativePrice {
        return NativePrice.fromNativePrice(this.option.price);
    }

    get priceId(): number | undefined {
        return this.option.price.id;
    }

    protected addPick(): void {
        const pickSource = this.pickSourceProvider.get(
            this.trackingSource,
            this.trackingPosition,
            undefined,
            undefined,
            undefined,
            undefined,
            this.trackingOptions,
        );
        this.pickId.parentLinkedEventId = this.parentLinkedEventId;

        this.store.dispatch(
            ExternalBetslipActions.addPick({
                pickId: this.pickId,
                tracking: pickSource,
                priceId: this.option.price.id,
                parentLinkedEventId: this.parentLinkedEventId,
            }),
        );
    }

    protected removePick(): void {
        this.store.dispatch(ExternalBetslipActions.removePick({ pickId: this.pickId }));
    }

    private getPriceType(priceType: ParticipantPriceType): PriceType {
        switch (priceType) {
            case ParticipantPriceType.Fixed:
                return PriceType.Fixed;
            case ParticipantPriceType.NoPrice:
                return PriceType.NoPrice;
            case ParticipantPriceType.StartingPrice:
                return PriceType.StartingPrice;
            default:
                return exhaustiveCheckGuard(priceType);
        }
    }
}
